import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Utils
import { UserAuth } from "../../../utils/AuthContext";
import { formatDate } from "../../../utils/toDate";

// Icons
import searchIcon from "../../../assets/search_icon.png";

// Components
import PageWrapper from "../../../components/PageWrapper";
import CustomInput from "../../../components/CustomInput";
import SearchFilter from "../../../components/SearchFilter";
import CustomButton from "../../../components/CustomButton";
import InformationBox from "../../../components/InformationBox";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../../components/table-component/RoundedTable";
import Loader from "../../../components/Loader";
import PageHeadline from "../../../components/PageHeadline";

// Services
import { getAllProductOrders } from "../../../services/productServices";

export default function CompletedProductOrders() {
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productOrders, setProductOrders] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Search states
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("orderNumber"),
    value: "orderNumber",
  });
  const [searchQuery, setSearchQuery] = useState(null);
  const [startDate, setStartDate] = useState(formatDate(new Date()));
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  // Fetch product orders
  const fetchProductOrders = async (params = {}) => {
    console.log("Fetching product orders with params:", params);
    setLoading(true);
    setError(null);

    try {
      // const organizationId = userData?.organizationId;
      // if (!organizationId) {
      //   throw new Error("Organization ID is required");
      // }

      console.log(params);

      const response = await getAllProductOrders({
        page: params.page || pagination.currentPage,
        limit: params.limit || 5,
        status: "CONFIRMED",
        orderNumber: params.searchType == "date" ? null : params.searchQuery,
        startDate: params.searchType == "date" ? params.searchQuery.from : null,
        endDate: params.searchType == "date" ? params.searchQuery.to : null,
        // searchQuery: params.searchQuery,
        // searchType: params.searchType,
        // organizationId: organizationId,
        // ...params,
      });

      if (response.data) {
        setProductOrders(response.data.productOrders || []);
        setPagination({
          currentPage: response.data.currentPage || 1,
          totalPages: response.data.totalPages || 1,
          totalRecords: response.data.totalRecords || 0,
        });
      }
    } catch (err) {
      console.error("Error fetching product orders:", err);
      setError(err.message || "Failed to fetch product orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductOrders({
      page: pagination.currentPage,
      limit: 5,
    });
  }, [pagination.currentPage]);

  const tableTitles = [
     {
      value: "orderNumber",
      keepOnMobile: true,
    },
    {
      value: "orderType",
      keepOnMobile: true,
    },
    {
      value: "Date",
      keepOnMobile: true,
    },
  ];

  const tableRows = productOrders?.map((order) => {
    return [
      {
        value: order.orderNumber,
        type: TABLE_CELL_TYPES.LINK,
        to: {
          pathname: `/product-order/${order.orderNumber}`,
        },
        bold: true,
        keepOnMobile: true,
      },
       {
        value:  t(order.type),
        type: TABLE_CELL_TYPES.TEXT,
        keepOnMobile: true,
      },
      {
        value: order.date,
        type: TABLE_CELL_TYPES.DATE,
        keepOnMobile: true,
      },
    ];
  });

  const handleSearchType = async (type) => {
    if (type.value === "orderNumber" || type.value === "status") {
      setStartDate(formatDate(new Date()));
      setEndDate(formatDate(new Date()));
    } else if (type.value === "date") {
      setSearchQuery("");
    } else {
      console.error("invalid search type");
    }

    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle?.value === "date") {
      searchBy = { from: startDate, to: endDate };
    } else {
      searchBy = query;
    }

    fetchProductOrders({
      searchQuery: searchBy,
      searchType: selectedTitle.value,
      page: 1, // Reset to first page when searching
    });
  };

  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_500 u-margin--top_500">
        <PageHeadline
          title="completedProductOrders"
          classList="u-margin--top_400"
        />

        <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end">
          <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 u-width_full-width o-column--horizontal-align_space-between">
            {selectedTitle.value === "date" ? (
              <div className="o-column-row-switcher o-column--gap-size_300 ">
                <CustomInput
                  name="date"
                  title={t("from")}
                  type="date"
                  thick
                  onChange={(date) => {
                    setStartDate(date?.target?.value);
                  }}
                  resetSearch={() => {
                    setStartDate(null);
                  }}
                  value={formatDate(startDate)}
                />

                <CustomInput
                  name="date"
                  title={t("to")}
                  type="date"
                  thick
                  onChange={(date) => {
                    setEndDate(date?.target?.value);
                  }}
                  resetSearch={() => {
                    setEndDate(null);
                  }}
                  value={formatDate(endDate)}
                />
              </div>
            ) : (
              <CustomInput
                placeholder={`${t("searchTitle")} ${t(selectedTitle?.value)}`}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                resetSearch={() => {
                  setSearchQuery(null);
                  handleSearch("");
                }}
                thick
                flex
              />
            )}
            <SearchFilter
              options={[
                {
                  title: t("orderNumber"),
                  value: "orderNumber",
                },
                {
                  title: t("Date"),
                  value: "date",
                },
              ]}
              selected={selectedTitle}
              setSelected={handleSearchType}
              thick
            />
          </div>

          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </section>

        {loading && <Loader />}

        {tableRows?.length ? (
          <section>
            <RoundedTable
              titles={tableTitles}
              rows={tableRows}
              pagination={pagination}
              setPagination={setPagination}
            />
          </section>
        ) : (
          <InformationBox>{t("noOrders")}</InformationBox>
        )}

        {error && (
          <InformationBox>
            {t("error")} {error}
          </InformationBox>
        )}
      </div>
    </PageWrapper>
  );
}
